/** biome-ignore-all lint/suspicious/noExplicitAny: temporary */
import { z } from 'zod';

import { CartesianSchema } from '#core/components/dashboard/panels/cartesian/schema.ts';
import { KpiSchema } from '#core/components/dashboard/panels/kpi/schema.ts';
import { ReferenceArea, ReferenceLine, TimelineReferenceLine } from '#core/components/dashboard/schema.annotations.ts';
import {
  columnRef,
  Dataset,
  type DatasetColumnT,
  type DatasetColumnTypeT,
  type DatasetT,
  inputDataset,
} from '#core/components/dashboard/schema.dataset.ts';
import {
  FillOpacity,
  GridMode,
  GridSpan,
  ItemBase,
  LegendPosition,
  NumericAxis,
  SeriesColor,
  Threshold,
  TimeAxis,
  ValueFormat,
} from '#core/components/dashboard/schema.primitives.ts';

// Design principles encoded in this schema:
//
// 1. Data is never inline. Every chart references columns of a `Dataset` keyed
//    in the top-level `datasets` map. The schema does the cross-validation
//    (existence, column-type compatibility, kind inference) via one root-level
//    `superRefine` — there is no external resolver step.
//
// 2. One unambiguous pattern per concept. Where a chart type has a `series`
//    array (cartesian, scatter, radar), every series owns its own
//    `inputDataset` + `data` role map — no item-level default, no override
//    semantics. Where a chart type has a single conceptual data source
//    (proportion, kpi, gauge, timeline, table),
//    `inputDataset` and `data` live directly on the chart item.
//
// 3. Implicit behavior. Presence/absence of a field decides what's enabled.
//    No flags that gate other fields. Examples:
//      - `SeriesData.min` and/or `.max` present → band auto-renders.
//      - `xAxis.kind` omitted → derived from the type of the referenced x columns.
//      - `yAxisRight` omitted → auto-created when any series uses axis="right".
//      - `RadarChart.axes` omitted → derived from distinct `data.axis` values.
//      - `KpiData.delta` omitted with sparkline present → auto-derived from sparkline.

// Series — Scatter

const ScatterSeriesData = z.object({
  x: columnRef.describe('Numeric column for x values.'),
  y: columnRef.describe('Numeric column for y values.'),
  size: columnRef.optional().describe('Numeric column. When present, points render as bubbles sized by this column.'),
  label: columnRef.optional().describe('Optional per-point label column (used for tooltips and labels).'),
});

const ScatterSeries = z.object({
  id: z.string(),
  label: z.string().optional(),
  color: SeriesColor.optional(),
  inputDataset,
  data: ScatterSeriesData,
});

// Series — Radar

const RadarSeriesData = z.object({
  axis: columnRef.describe('Column whose distinct values become polygon vertices.'),
  value: columnRef.describe('Numeric column for radial magnitudes (one value per vertex).'),
});

const RadarSeries = z.object({
  id: z.string(),
  label: z.string().optional(),
  color: SeriesColor.optional(),
  fillOpacity: FillOpacity.default('light'),
  inputDataset,
  data: RadarSeriesData,
});

// Single-source data shapes (item-level inputDataset)

const ProportionData = z.object({
  label: columnRef.describe('Column for the slice label.'),
  value: columnRef.describe('Numeric, non-negative column for the slice value.'),
  color: columnRef.optional().describe('Optional column of SeriesColor values; auto-assigned when omitted.'),
});

const GaugeData = z.object({
  value: columnRef.describe('Numeric column. Latest non-null value is rendered on the gauge.'),
});

const TimelineData = z.object({
  rowId: columnRef.describe('Column whose distinct values define timeline rows.'),
  rowLabel: columnRef
    .optional()
    .describe(
      'Optional column with a human-readable row label (first non-null value per rowId is used). ' +
        'Defaults to the rowId value when omitted.',
    ),
  start: columnRef.describe('Column of ISO 8601 datetime values for segment starts.'),
  end: columnRef.describe('Column of ISO 8601 datetime values for segment ends.'),
  label: columnRef.optional().describe('Optional column for per-segment labels.'),
  color: columnRef
    .optional()
    .describe('Optional column of SeriesColor values for per-segment color; auto-assigned when omitted.'),
});

// Tables

const TableColumn = z.object({
  id: z.string().describe('Stable column identifier; doubles as the dataset column name when `source` is omitted.'),
  source: columnRef.optional().describe('Dataset column to project; defaults to `id`.'),
  label: z.string().optional().describe('Display header; defaults to the source column name.'),
  format: z
    .enum(['text', 'number', 'percent', 'currency', 'datetime', 'duration-ms', 'sparkline'])
    .optional()
    .describe(
      'Cell rendering format. Auto-derived from the dataset column type when omitted ' +
        '(number → "number", datetime → "datetime", otherwise "text"). ' +
        'Use "sparkline" when the source column carries array-valued cells (e.g. produced by ' +
        'daily() / monthly() / datapoints() derives).',
    ),
  align: z.enum(['left', 'right', 'center']).default('left'),
  colorByValue: z
    .boolean()
    .default(false)
    .describe('Numeric/percent columns: cell background tinted by value (heatmap-style).'),
});

// Chart items

const ScatterChart = ItemBase.extend({
  type: z.literal('scatter'),
  xAxis: NumericAxis.prefault({}),
  yAxis: NumericAxis.prefault({}),
  series: ScatterSeries.array().min(1).max(6),
  referenceLines: ReferenceLine.array().prefault([]),
  referenceAreas: ReferenceArea.array().prefault([]),
  grid: GridMode.default('both'),
  legend: LegendPosition.default('bottom'),
});

const ProportionChart = ItemBase.extend({
  type: z.literal('proportion'),
  variant: z.enum(['pie', 'donut', 'funnel', 'radialBar']),
  inputDataset,
  data: ProportionData,
  legend: LegendPosition.default('bottom'),
  showLabels: z.boolean().default(true),
  showRatios: z
    .boolean()
    .default(false)
    .describe('pie/donut: shows % of total. funnel: shows % drop between stages. radialBar: shows % per bar.'),
  centerLabel: z.string().optional().describe('donut variant only; ignored otherwise.'),
});

const GaugeChart = ItemBase.extend({
  type: z.literal('gauge'),
  inputDataset,
  data: GaugeData,
  format: ValueFormat.default('number'),
  rangeMin: z.number().default(0),
  rangeMax: z.number(),
  thresholds: Threshold.array().max(4).optional().describe('Color-zone breakpoints across the gauge range.'),
});

const RadarChart = ItemBase.extend({
  type: z.literal('radar'),
  series: RadarSeries.array().min(1).max(4),
  axes: columnRef
    .array()
    .min(3)
    .max(12)
    .optional()
    .describe(
      'Optional explicit vertex order. When omitted, vertices are derived from distinct ' +
        'values of `data.axis` across all series, in appearance order.',
    ),
  legend: LegendPosition.default('bottom'),
});

const TimelineChart = ItemBase.extend({
  type: z.literal('timeline'),
  inputDataset,
  data: TimelineData,
  xAxis: TimeAxis.prefault({}),
  referenceLines: TimelineReferenceLine.array().prefault([]),
});

export const TableChart = ItemBase.extend({
  type: z.literal('table'),
  inputDataset,
  columns: TableColumn.array().min(1),
});

// Item union

export const DashboardItem = z.discriminatedUnion('type', [
  CartesianSchema,
  ScatterChart,
  ProportionChart,
  KpiSchema,
  GaugeChart,
  RadarChart,
  TimelineChart,
  TableChart,
]);

type DashboardItemT = z.infer<typeof DashboardItem>;
type ItemByType<K extends DashboardItemT['type']> = Extract<DashboardItemT, { type: K }>;

// Validation context — built once per superRefine call. Each per-chart
// validator receives this and the item's path prefix.

type IssueFn = (path: (string | number)[], message: string) => void;

type ValidationCtx = {
  datasets: Map<string, DatasetT>;
  issue: IssueFn;
  resolveColumn: (datasetKey: string, columnName: string, path: (string | number)[]) => DatasetColumnT | undefined;
  expectColumnType: (
    datasetKey: string,
    columnName: string,
    expected: readonly DatasetColumnTypeT[],
    path: (string | number)[],
  ) => void;
};

function buildValidationCtx(datasets: Map<string, DatasetT>, ctx: z.RefinementCtx): ValidationCtx {
  const columnIndex = new Map<string, Map<string, DatasetColumnT>>();
  for (const [key, ds] of datasets.entries()) {
    const index = new Map<string, DatasetColumnT>();
    for (const col of ds.columns) {
      index.set(col.name, col);
    }
    columnIndex.set(key, index);
  }

  const issue: IssueFn = (path, message) => ctx.addIssue({ code: 'custom', message, path });

  const resolveColumn: ValidationCtx['resolveColumn'] = (datasetKey, columnName, path) => {
    const index = columnIndex.get(datasetKey);
    if (index === undefined) {
      issue(path, `unknown inputDataset "${datasetKey}"`);
      return undefined;
    }
    const col = index.get(columnName);
    // FIXME: evaluate derive expression on dataset
    // if (col === undefined) {
    //   issue(path, `column "${columnName}" not found in dataset "${datasetKey}"`);
    //   return undefined;
    // }
    return col;
  };

  const expectColumnType: ValidationCtx['expectColumnType'] = (datasetKey, columnName, expected, path) => {
    const col = resolveColumn(datasetKey, columnName, path);
    if (col === undefined) {
      return;
    }
    if (!expected.includes(col.type)) {
      issue(path, `column "${columnName}" has type "${col.type}"; expected one of: ${expected.join(', ')}`);
    }
  };

  return { datasets, issue, resolveColumn, expectColumnType };
}

function columnTypeToAxisKind(t: DatasetColumnTypeT): 'time' | 'category' | 'numeric' | undefined {
  if (t === 'datetime') {
    return 'time';
  }
  if (t === 'number') {
    return 'numeric';
  }
  if (t === 'string') {
    return 'category';
  }
  return undefined;
}

function distinctStringValues(ds: DatasetT, columnName: string): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const row of ds.rows) {
    const v = row[columnName];
    if (v === null || v === undefined) {
      continue;
    }
    const key = String(v);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    ordered.push(key);
  }
  return ordered;
}

// Per-chart validators. Each starts at nesting depth 1 (its own function body).

function validateCartesian(item: ItemByType<'cartesian'>, base: (string | number)[], v: ValidationCtx) {
  const seriesPath = (si: number, ...rest: (string | number)[]) => [...base, 'series', si, ...rest];
  const xTypes: DatasetColumnTypeT[] = [];

  item.series.forEach((s, si) => {
    const xCol = v.resolveColumn(s.inputDataset, s.data.x, seriesPath(si, 'data', 'x'));
    const yCol = v.resolveColumn(s.inputDataset, s.data.y, seriesPath(si, 'data', 'y'));
    if (xCol !== undefined) {
      xTypes.push(xCol.type);
    }
    if (yCol !== undefined && yCol.type !== 'number') {
      v.issue(seriesPath(si, 'data', 'y'), `y column must be numeric; "${s.data.y}" has type "${yCol.type}"`);
    }
    if (s.data.min !== undefined) {
      v.expectColumnType(s.inputDataset, s.data.min, ['number'], seriesPath(si, 'data', 'min'));
    }
    if (s.data.max !== undefined) {
      v.expectColumnType(s.inputDataset, s.data.max, ['number'], seriesPath(si, 'data', 'max'));
    }
  });

  const distinctX = new Set(xTypes);
  if (distinctX.size > 1) {
    v.issue([...base, 'series'], `inconsistent x column types across series: ${[...distinctX].join(', ')}`);
  }

  const derivedKind = xTypes.length > 0 ? columnTypeToAxisKind(xTypes[0] ?? 'string') : undefined;
  const declaredKind = item.xAxis.kind;
  const resolvedKind = declaredKind ?? derivedKind;

  if (
    declaredKind !== undefined &&
    derivedKind !== undefined &&
    declaredKind !== derivedKind &&
    declaredKind !== 'category'
  ) {
    v.issue(
      [...base, 'xAxis', 'kind'],
      `xAxis.kind="${declaredKind}" inconsistent with x column type "${xTypes[0]}" (would imply "${derivedKind}")`,
    );
  }

  const checkRefValue = (value: unknown, path: (string | number)[]) => {
    if (resolvedKind === undefined) {
      return;
    }
    const t = typeof value;
    if (resolvedKind === 'numeric' && t !== 'number') {
      v.issue(path, 'value must be a number when xAxis is numeric');
    } else if ((resolvedKind === 'time' || resolvedKind === 'category') && t !== 'string') {
      v.issue(path, `value must be a string when xAxis is ${resolvedKind}`);
    }
  };
  item.referenceLines.forEach((rl, i) => {
    if (rl.axis === 'x') {
      checkRefValue(rl.value, [...base, 'referenceLines', i, 'value']);
    }
  });
  item.referenceAreas.forEach((ra, i) => {
    if (ra.axis !== 'x') {
      return;
    }
    checkRefValue(ra.start, [...base, 'referenceAreas', i, 'start']);
    checkRefValue(ra.end, [...base, 'referenceAreas', i, 'end']);
  });
}

function validateScatter(item: ItemByType<'scatter'>, base: (string | number)[], v: ValidationCtx) {
  const seriesPath = (si: number, ...rest: (string | number)[]) => [...base, 'series', si, ...rest];

  item.series.forEach((s, si) => {
    v.expectColumnType(s.inputDataset, s.data.x, ['number'], seriesPath(si, 'data', 'x'));
    v.expectColumnType(s.inputDataset, s.data.y, ['number'], seriesPath(si, 'data', 'y'));
    if (s.data.size !== undefined) {
      v.expectColumnType(s.inputDataset, s.data.size, ['number'], seriesPath(si, 'data', 'size'));
    }
    if (s.data.label !== undefined) {
      v.resolveColumn(s.inputDataset, s.data.label, seriesPath(si, 'data', 'label'));
    }
  });

  item.referenceLines.forEach((rl, i) => {
    if (typeof rl.value !== 'number') {
      v.issue([...base, 'referenceLines', i, 'value'], 'scatter reference values must be numeric');
    }
  });
  item.referenceAreas.forEach((ra, i) => {
    if (typeof ra.start !== 'number') {
      v.issue([...base, 'referenceAreas', i, 'start'], 'scatter reference values must be numeric');
    }
    if (typeof ra.end !== 'number') {
      v.issue([...base, 'referenceAreas', i, 'end'], 'scatter reference values must be numeric');
    }
  });
}

function validateProportion(item: ItemByType<'proportion'>, base: (string | number)[], v: ValidationCtx) {
  v.resolveColumn(item.inputDataset, item.data.label, [...base, 'data', 'label']);
  v.expectColumnType(item.inputDataset, item.data.value, ['number'], [...base, 'data', 'value']);
  if (item.data.color !== undefined) {
    v.expectColumnType(item.inputDataset, item.data.color, ['string'], [...base, 'data', 'color']);
  }
}

function validateKpi(item: ItemByType<'kpi'>, base: (string | number)[], v: ValidationCtx) {
  v.expectColumnType(item.inputDataset, item.data.value, ['number'], [...base, 'data', 'value']);
  if (item.data.delta !== undefined) {
    v.expectColumnType(item.inputDataset, item.data.delta, ['number'], [...base, 'data', 'delta']);
  }
  if (item.data.sparkline !== undefined) {
    v.resolveColumn(item.inputDataset, item.data.sparkline.x, [...base, 'data', 'sparkline', 'x']);
    v.expectColumnType(item.inputDataset, item.data.sparkline.y, ['number'], [...base, 'data', 'sparkline', 'y']);
  }
}

function validateGauge(item: ItemByType<'gauge'>, base: (string | number)[], v: ValidationCtx) {
  v.expectColumnType(item.inputDataset, item.data.value, ['number'], [...base, 'data', 'value']);
  if (item.rangeMax <= item.rangeMin) {
    v.issue([...base, 'rangeMax'], 'rangeMax must be greater than rangeMin');
  }
}

function validateRadar(item: ItemByType<'radar'>, base: (string | number)[], v: ValidationCtx) {
  const seriesPath = (si: number, ...rest: (string | number)[]) => [...base, 'series', si, ...rest];
  const axisValueSets: { si: number; values: string[] }[] = [];

  item.series.forEach((s, si) => {
    const axisCol = v.resolveColumn(s.inputDataset, s.data.axis, seriesPath(si, 'data', 'axis'));
    v.expectColumnType(s.inputDataset, s.data.value, ['number'], seriesPath(si, 'data', 'value'));
    if (axisCol === undefined) {
      return;
    }
    const ds = v.datasets.get(s.inputDataset);
    if (ds === undefined) {
      return;
    }
    axisValueSets.push({ si, values: distinctStringValues(ds, s.data.axis) });
  });

  if (axisValueSets.length >= 2) {
    const referenceSorted = [...(axisValueSets[0]?.values ?? [])].sort().join('|');
    for (let i = 1; i < axisValueSets.length; i++) {
      const candidate = [...(axisValueSets[i]?.values ?? [])].sort().join('|');
      if (candidate === referenceSorted) {
        continue;
      }
      v.issue(
        seriesPath(axisValueSets[i]?.si ?? 0, 'data', 'axis'),
        'all radar series must yield the same set of distinct axis values',
      );
    }
  }

  if (item.axes !== undefined && axisValueSets.length > 0) {
    const axesSet = new Set(item.axes);
    for (const value of axisValueSets[0]?.values ?? []) {
      if (axesSet.has(value)) {
        continue;
      }
      v.issue([...base, 'axes'], `data.axis value "${value}" is missing from explicit axes`);
    }
  }
}

function validateTimeline(item: ItemByType<'timeline'>, base: (string | number)[], v: ValidationCtx) {
  const d = item.data;
  v.resolveColumn(item.inputDataset, d.rowId, [...base, 'data', 'rowId']);
  v.expectColumnType(item.inputDataset, d.start, ['datetime', 'string'], [...base, 'data', 'start']);
  v.expectColumnType(item.inputDataset, d.end, ['datetime', 'string'], [...base, 'data', 'end']);
  if (d.rowLabel !== undefined) {
    v.resolveColumn(item.inputDataset, d.rowLabel, [...base, 'data', 'rowLabel']);
  }
  if (d.label !== undefined) {
    v.resolveColumn(item.inputDataset, d.label, [...base, 'data', 'label']);
  }
  if (d.color !== undefined) {
    v.expectColumnType(item.inputDataset, d.color, ['string'], [...base, 'data', 'color']);
  }

  const ds = v.datasets.get(item.inputDataset);
  if (ds === undefined) {
    return;
  }
  ds.rows.forEach((row, ri) => {
    const s = row[d.start];
    const e = row[d.end];
    if (typeof s !== 'string' || typeof e !== 'string') {
      return;
    }
    if (new Date(e) <= new Date(s)) {
      v.issue([...base, 'data'], `row ${ri}: end ("${e}") must be after start ("${s}")`);
    }
  });
}

function validateTable(item: ItemByType<'table'>, base: (string | number)[], v: ValidationCtx) {
  item.columns.forEach((col, ci) => {
    const sourceName = col.source ?? col.id;
    const colPath = [...base, 'columns', ci, col.source === undefined ? 'id' : 'source'];
    v.resolveColumn(item.inputDataset, sourceName, colPath);
  });
}

// Typed dispatch table. Each entry is narrowed to the matching item shape.

type ItemValidators = {
  [K in DashboardItemT['type']]: (item: ItemByType<K>, base: (string | number)[], v: ValidationCtx) => void;
};

const itemValidators: ItemValidators = {
  cartesian: validateCartesian,
  scatter: validateScatter,
  proportion: validateProportion,
  kpi: validateKpi,
  gauge: validateGauge,
  radar: validateRadar,
  timeline: validateTimeline,
  table: validateTable,
};

// Root schema with integrated dataset cross-validation

const DashboardToolShape = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  grid: z.object({ columns: GridSpan.default(2) }),
  datasets: z
    .map(z.string(), Dataset)
    .describe('Map of dataset key → Dataset. Items reference these via `inputDataset`.'),
  items: DashboardItem.array().min(1).max(12),
});

/**
 * LLM-facing input shape: identical to {@link DashboardTool} but with the
 * `datasets` map omitted. The chat plugin's resolver layer merges staged
 * datasets in before running full validation against {@link DashboardTool}.
 */
export const DashboardToolLlmInput = DashboardToolShape.omit({ datasets: true });

export const DashboardTool = DashboardToolShape.superRefine((tool, ctx) => {
  const v = buildValidationCtx(tool.datasets, ctx);
  tool.items.forEach((item, ii) => {
    const base: (string | number)[] = ['items', ii];
    // The dispatch table is narrowed per-key, but TS can't follow narrowing
    // through a dynamic lookup — cast at the boundary.
    const validator = itemValidators[item.type] as (i: typeof item, b: (string | number)[], v: ValidationCtx) => void;
    validator(item, base, v);
  });
});

export type DashboardToolInput = z.infer<typeof DashboardTool>;
export type DashboardToolLlmInputType = z.infer<typeof DashboardToolLlmInput>;
export type DashboardItemInput = z.infer<typeof DashboardItem> & { datasets: Map<string, DatasetT> };
